import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { useAuth } from "@/hooks/useAuth";
import { usePolicyGateCheck } from "@/hooks/usePolicies";
import { hasRole } from "@/lib/auth";
import { isComplianceEnabled } from "@/lib/features";

export const POLICY_GATE_PATH = "/policies/acknowledge";

/**
 * Route guard for the policy acceptance gate (BE #864).
 *
 * The backend sets `has_pending_policies` on the user (login, session restore,
 * `/users/me`) when a consultant has a published policy that is pending or
 * outdated. Admins are never flagged, and nothing is flagged while no policy
 * is published. For an already open session the guard also re-checks
 * `/policies` periodically and on window focus, so a policy published
 * mid-session is caught too. Gated users are sent to the blocking
 * acknowledgement screen and come back to where they were going afterwards.
 *
 * A no-op while the Policies feature is off (`features.compliance`, BE #898):
 * no re-check is made and nobody is redirected.
 */
export const PolicyGateGuard = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();
  const compliance = isComplianceEnabled(user);
  const { data: gateRequired } = usePolicyGateCheck(
    compliance && !!user && !hasRole(user, "ADMIN")
  );

  if (compliance && (user?.has_pending_policies || gateRequired)) {
    return <Navigate to={POLICY_GATE_PATH} state={{ from: location }} replace />;
  }
  return <>{children}</>;
};
